import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { X, Upload, Layers } from 'lucide-react';
import {
    createCategoryConfig, updateCategoryConfig, listCategories, listAssets,
    uploadAsset, type SocialCategoryConfig, type SocialAsset,
} from '@/services/api/social-publisher';


const TONE_OPTIONS = [
    'descontraído', 'próximo', 'motivacional', 'inspirador', 'informativo',
    'profissional', 'divertido', 'reflexivo', 'urgente', 'empático',
];

const HASHTAG_PRESETS = [
    '#dicas', '#motivacao', '#inspiracao', '#vida', '#lifestyle', '#brasil',
    '#mindset', '#sucesso', '#foco', '#conquista', '#amor', '#familia',
    '#marketing', '#empreendedorismo', '#negocios', '#vendas', '#marca', '#estrategia',
    '#saude', '#bemestar', '#alimentacao', '#fitness',
    '#educacao', '#aprendizado', '#conteudo', '#blog',
    '#beleza', '#moda', '#estilo', '#tendencia',
];


function formatTimeMask(raw: string): string {
    // Extrai só dígitos e formata como HH:MM (max 4 dígitos)
    const digits = raw.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function TimeChips({
    label, value, onChange, color = 'purple',
}: {
    label: string; value: string[];
    onChange: (next: string[]) => void;
    color?: 'purple' | 'fuchsia';
}) {
    const [draft, setDraft] = useState('');
    const valid = /^([01]\d|2[0-3]):[0-5]\d$/.test(draft);
    const add = () => {
        if (!valid || value.includes(draft)) return;
        onChange([...value, draft].sort());
        setDraft('');
    };
    const remove = (t: string) => onChange(value.filter((v) => v !== t));
    const chip = color === 'purple'
        ? 'bg-purple-100 text-purple-700 border-purple-200'
        : 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200';
    return (
        <div>
            <Label className="text-xs text-gray-500">{label}</Label>
            <div className="mt-1 flex flex-wrap gap-1.5 min-h-[24px]">
                {value.length === 0 && (
                    <span className="text-xs text-gray-400">Nenhum horário ainda</span>
                )}
                {value.map((t) => (
                    <span
                        key={t}
                        className={`text-xs px-2 py-0.5 rounded-full border ${chip} inline-flex items-center gap-1`}
                    >
                        {t}
                        <button onClick={() => remove(t)} className="hover:text-red-600">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="mt-2">
                <Input
                    placeholder="ex: 08:00"
                    value={draft}
                    onChange={(e) => setDraft(formatTimeMask(e.target.value))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); add(); }
                    }}
                    onBlur={() => { if (valid) add(); }}
                    inputMode="numeric"
                    maxLength={5}
                    className="h-8 text-sm"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                    Digite os números e Enter (ou clique fora) pra adicionar.
                </p>
            </div>
        </div>
    );
}


function AssetSelect({
    label, value, onChange, kind, helperText,
}: {
    label: string;
    value: string;
    onChange: (id: string) => void;
    kind: 'logo' | 'model_photo';
    helperText?: string;
}) {
    const qc = useQueryClient();
    const { toast } = useToast();
    const fileRef = useRef<HTMLInputElement>(null);
    const { data: assets = [] } = useQuery({
        queryKey: ['social-publisher', 'assets', kind],
        queryFn: () => listAssets({ kind }),
    });
    const current = assets.find((a: SocialAsset) => String(a.id) === value);

    const up = useMutation({
        mutationFn: (file: File) => uploadAsset(file, kind),
        onSuccess: (asset) => {
            toast({ title: 'Upload OK' });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'assets'] });
            onChange(String(asset.id));
            if (fileRef.current) fileRef.current.value = '';
        },
        onError: (e: any) => {
            toast({
                title: 'Erro upload',
                description: e?.response?.data?.detail || 'Falha',
                variant: 'destructive',
            });
        },
    });

    return (
        <div>
            <Label className="text-xs text-gray-500">{label}</Label>
            <div className="flex gap-2">
                <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="(nenhum)" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">(nenhum)</SelectItem>
                        {assets.map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                                {a.file_path.split('/').pop()}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) up.mutate(f);
                    }}
                    className="hidden"
                />
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={up.isPending}
                    className="px-3"
                >
                    <Upload className="w-3.5 h-3.5" />
                </Button>
            </div>
            {current && (
                <img
                    src={current.file_url}
                    alt={label}
                    className="mt-2 max-h-16 rounded border"
                />
            )}
            {helperText && <p className="text-[11px] text-gray-400 mt-1">{helperText}</p>}
        </div>
    );
}


interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    editing: SocialCategoryConfig | null;
    onSaved: () => void;
}

export default function CategoryConfigForm({ open, onOpenChange, editing, onSaved }: Props) {
    const { toast } = useToast();
    const [categoryId, setCategoryId] = useState<string>('');
    const [language, setLanguage] = useState('pt-BR');
    const [tone, setTone] = useState('');
    const [hashtags, setHashtags] = useState<string[]>([]);
    const [outfitStyle, setOutfitStyle] = useState('');
    const [logoId, setLogoId] = useState<string>('');
    const [photoId, setPhotoId] = useState<string>('');
    const [storyTimes, setStoryTimes] = useState<string[]>([]);
    const [postTimes, setPostTimes] = useState<string[]>([]);
    const [horizon, setHorizon] = useState('7');
    const [maxPerDay, setMaxPerDay] = useState('5');
    const [status, setStatus] = useState('active');

    const { data: categories = [], isLoading: loadingCats } = useQuery({
        queryKey: ['social-publisher', 'categories'],
        queryFn: listCategories,
        enabled: open,
    });

    const selectedCategory = categories.find((c) => String(c.id) === categoryId);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setCategoryId(String(editing.category_id));
            const pj: any = editing.persona_json || {};
            setLanguage(pj.language || 'pt-BR');
            setTone(pj.tone || '');
            setHashtags(
                Array.isArray(pj.hashtags)
                    ? pj.hashtags
                    : (typeof pj.hashtags === 'string' ? pj.hashtags.split(/\s+/).filter(Boolean) : []),
            );
            setOutfitStyle(pj.outfit_style || '');
            setLogoId(editing.logo_asset_id ? String(editing.logo_asset_id) : '');
            setPhotoId(editing.model_photo_asset_id ? String(editing.model_photo_asset_id) : '');
            setStoryTimes(editing.story_schedule_times || []);
            setPostTimes(editing.post_schedule_times || []);
            setHorizon(String(editing.horizon_days || 7));
            setMaxPerDay(String(editing.max_per_day || 5));
            setStatus(editing.status || 'active');
        } else {
            setCategoryId('');
            setLanguage('pt-BR');
            setTone(''); setHashtags([]); setOutfitStyle('');
            setLogoId(''); setPhotoId('');
            setStoryTimes(['08:00', '12:00', '22:00']);
            setPostTimes(['12:00', '20:00']);
            setHorizon('7'); setMaxPerDay('5');
            setStatus('active');
        }
    }, [open, editing]);

    const mutation = useMutation({
        mutationFn: async () => {
            const persona_json = {
                language,
                tone: tone || undefined,
                hashtags,
                outfit_style: outfitStyle.trim() || undefined,
            };
            const personaName = selectedCategory?.name
                || editing?.persona_name
                || null;
            const payload = {
                category_id: Number(categoryId),
                persona_name: personaName,
                persona_json,
                logo_asset_id: logoId ? Number(logoId) : null,
                model_photo_asset_id: photoId ? Number(photoId) : null,
                story_schedule_times: storyTimes,
                post_schedule_times: postTimes,
                horizon_days: Number(horizon),
                max_per_day: Number(maxPerDay),
                status,
            };
            return editing
                ? updateCategoryConfig(editing.id, payload)
                : createCategoryConfig(payload);
        },
        onSuccess: () => {
            toast({ title: editing ? 'Atualizada' : 'Criada' });
            onSaved();
        },
        onError: (e: any) => {
            toast({
                title: 'Erro',
                description: e?.response?.data?.detail || 'Falha ao salvar',
                variant: 'destructive',
            });
        },
    });

    const canSave = !!categoryId && (storyTimes.length + postTimes.length > 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden border-none rounded-2xl">
                <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h3 className="text-base font-semibold text-gray-800">
                            {editing ? 'Editar configuração de categoria' : 'Configurar categoria'}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Persona única aplicada a todas as páginas da categoria (com override
                            opcional em cada página).
                        </p>
                    </div>

                    <Tabs defaultValue="category" className="px-6 pt-3">
                        <TabsList>
                            <TabsTrigger value="category">Categoria & persona</TabsTrigger>
                            <TabsTrigger value="schedule">Horários</TabsTrigger>
                        </TabsList>

                        <div className="py-4 max-h-[60vh] overflow-y-auto">
                            <TabsContent value="category" className="space-y-4 mt-0">
                                <div>
                                    <Label className="text-xs text-gray-500">Categoria</Label>
                                    {editing ? (
                                        <div className="text-sm px-3 py-2 border rounded bg-gray-50 text-gray-600">
                                            {selectedCategory?.name || `Categoria #${categoryId}`}
                                        </div>
                                    ) : (
                                        <SearchableSelect
                                            value={categoryId}
                                            onValueChange={setCategoryId}
                                            placeholder={
                                                loadingCats
                                                    ? 'Carregando categorias...'
                                                    : 'Selecione uma categoria'
                                            }
                                            searchPlaceholder="Buscar categoria..."
                                            options={categories.map((c) => ({
                                                value: String(c.id),
                                                label: `${c.name} (${c.valid_pages_count}/${c.pages_count} pages)`,
                                            }))}
                                        />
                                    )}
                                </div>

                                {editing && (
                                    <div>
                                        <Label className="text-xs text-gray-500">Status</Label>
                                        <div className="flex gap-2 mt-1">
                                            {([
                                                { v: 'active', label: 'Ativa', on: 'bg-emerald-600 border-emerald-600 text-white', off: 'text-emerald-700 border-emerald-200 hover:bg-emerald-50' },
                                                { v: 'paused', label: 'Pausada', on: 'bg-amber-500 border-amber-500 text-white', off: 'text-amber-700 border-amber-200 hover:bg-amber-50' },
                                                { v: 'archived', label: 'Arquivada', on: 'bg-gray-600 border-gray-600 text-white', off: 'text-gray-600 border-gray-200 hover:bg-gray-50' },
                                            ] as const).map((o) => (
                                                <button
                                                    key={o.v}
                                                    type="button"
                                                    onClick={() => setStatus(o.v)}
                                                    className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${status === o.v ? o.on : `bg-white ${o.off}`}`}
                                                >
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            Pausada/Arquivada: para de gerar e agendar posts pra TODAS as páginas desta categoria (inclusive as com override). Não apaga o que já existe.
                                        </p>
                                    </div>
                                )}

                                {selectedCategory && (
                                    <div className="flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl p-3">
                                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                            <Layers className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-800 truncate">
                                                {selectedCategory.name}
                                            </div>
                                            <div className="text-[11px] text-gray-500">
                                                {selectedCategory.valid_pages_count} pages c/ token
                                                válido · {selectedCategory.pages_count} totais
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-2 border-t">
                                    <div className="text-xs font-medium text-gray-700 mb-2">
                                        🎭 Persona desta categoria
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-gray-500">Idioma</Label>
                                            <Select value={language} onValueChange={setLanguage}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="pt-BR">Português (BR)</SelectItem>
                                                    <SelectItem value="en-US">English (US)</SelectItem>
                                                    <SelectItem value="es-ES">Español</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-gray-500">Tom</Label>
                                            <Select value={tone || 'none'} onValueChange={(v) => setTone(v === 'none' ? '' : v)}>
                                                <SelectTrigger><SelectValue placeholder="(sem tom específico)" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">(sem tom específico)</SelectItem>
                                                    {TONE_OPTIONS.map((t) => (
                                                        <SelectItem key={t} value={t}>{t}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        <Label className="text-xs text-gray-500">
                                            Vestimenta / estilo visual (opcional)
                                        </Label>
                                        <Input
                                            placeholder='ex: "roupa sensual, lingerie ou biquíni"'
                                            value={outfitStyle}
                                            onChange={(e) => setOutfitStyle(e.target.value)}
                                        />
                                        <p className="text-[11px] text-gray-400 mt-1">
                                            Aplicado a todas as imagens geradas — descreva a roupa
                                            que a modelo deve usar. Sobrescreve o padrão neutro.
                                        </p>
                                    </div>

                                    <div className="mt-3">
                                        <Label className="text-xs text-gray-500">
                                            Hashtags padrão{' '}
                                            <span className="text-gray-400">({hashtags.length} selecionadas)</span>
                                        </Label>
                                        <div className="mt-1 flex flex-wrap gap-1.5 p-3 bg-gray-50 rounded-lg border">
                                            {HASHTAG_PRESETS.map((h) => {
                                                const active = hashtags.includes(h);
                                                return (
                                                    <button
                                                        key={h}
                                                        type="button"
                                                        onClick={() => {
                                                            setHashtags((prev) =>
                                                                prev.includes(h)
                                                                    ? prev.filter((x) => x !== h)
                                                                    : [...prev, h],
                                                            );
                                                        }}
                                                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                                            active
                                                                ? 'bg-purple-600 text-white border-purple-600'
                                                                : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'
                                                        }`}
                                                    >
                                                        {h}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 mt-3">
                                        <AssetSelect
                                            label="Logo (opcional)"
                                            value={logoId}
                                            onChange={setLogoId}
                                            kind="logo"
                                            helperText="Clique no botão pra upload direto"
                                        />
                                        <AssetSelect
                                            label="Foto da pessoa-marca"
                                            value={photoId}
                                            onChange={setPhotoId}
                                            kind="model_photo"
                                            helperText={
                                                !photoId
                                                    ? 'Sem foto → fallback usa a foto de cada página'
                                                    : undefined
                                            }
                                        />
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="schedule" className="space-y-4 mt-0">
                                <div className="grid grid-cols-2 gap-4">
                                    <TimeChips
                                        label="Horários de POST (feed)"
                                        value={postTimes}
                                        onChange={setPostTimes}
                                        color="purple"
                                    />
                                    <TimeChips
                                        label="Horários de STORY"
                                        value={storyTimes}
                                        onChange={setStoryTimes}
                                        color="fuchsia"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs text-gray-500">Horizonte (dias)</Label>
                                        <Input type="number" value={horizon} onChange={(e) => setHorizon(e.target.value)} />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-gray-500">Max/dia por page</Label>
                                        <Input type="number" value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value)} />
                                    </div>
                                </div>
                            </TabsContent>
                        </div>
                    </Tabs>

                    <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50/50">
                        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => mutation.mutate()}
                            disabled={!canSave || mutation.isPending}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {mutation.isPending ? 'Salvando...' : (editing ? 'Atualizar' : 'Criar')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
